// comestibleDraft.js — P3-5b. Modelo de datos de la sección COMESTIBLE de la ficha y
// su traducción al «modelo de Neto» que consume el motor (backend/consumibles/, el
// mismo que validó/creó la Barra de Proteína en P3-5a). La sección es ADITIVA al
// producto base: nombre/descripción/imágenes/estado/relacionados salen del `form`
// común; acá viven subtítulo, ejes sabor/pack (con foto/hex/descriptor/sello), las
// variaciones (sabor×pack) y las 13 metas del arquetipo. Frontend-only → export ES.

export const SELLOS_810 = [
  { id: 'azucares', label: 'Alto en azúcares' },
  { id: 'sodio', label: 'Alto en sodio' },
  { id: 'grasas-saturadas', label: 'Alto en grasas saturadas' },
  { id: 'grasas-trans', label: 'Alto en grasas trans' },
  { id: 'edulcorantes', label: 'Contiene edulcorantes' },
];

export function emptyComestible() {
  return {
    subtitulo: '',
    sabores: [],   // [{ nombre, descriptor, hex, thumbnailId }]
    packs: [],     // [{ nombre, sublabel, sello }]
    // Grid de precio/stock/imagen por combinación, clave `${sabor}∷${pack}`.
    variaciones: {},
    nutricion: { porcion: '', porciones: '', filas: [] }, // filas: [{nutriente,cantidad,unidad,vd}]
    ingredientes: '',
    alergenos: '',
    comoUsar: '',
    beneficiosDetalle: '',
    sellos: [],    // subconjunto de SELLOS_810[].id
    faq: [],       // [{ pregunta, respuesta }]
    beneficios: [], // [{ imagen, titulo, texto }]
    confianza: { icono: '', imagen: 0, titulo: '', fecha: '', texto: '', enlace_texto: '', enlace_url: '' },
    video: '',
    videoPortada: 0,
  };
}

export const claveVariacion = (sabor, pack) => `${sabor}∷${pack}`;

const vacio = (s) => !String(s == null ? '' : s).trim();

// Traduce el estado de la sección + el producto base al MODELO DE NETO del motor.
// Poda las metas opcionales vacías: en una actualización, mandar '' / [] BORRA la meta
// (armarDocumento), así que una meta sin contenido NO viaja.
export function comestibleAModelo(form = {}, c = emptyComestible()) {
  const sabores = (c.sabores || []).filter((s) => !vacio(s.nombre)).map((s) => ({
    nombre: s.nombre.trim(),
    ...(vacio(s.descriptor) ? {} : { descriptor: s.descriptor.trim() }),
    ...(vacio(s.hex) ? {} : { hex: s.hex.trim() }),
    ...(s.thumbnailId ? { thumbnailId: Number(s.thumbnailId) } : {}),
  }));
  const packs = (c.packs || []).filter((p) => !vacio(p.nombre)).map((p) => ({
    nombre: p.nombre.trim(),
    ...(vacio(p.sublabel) ? {} : { sublabel: p.sublabel.trim() }),
    ...(vacio(p.sello) ? {} : { sello: p.sello.trim() }),
  }));
  // Variaciones = producto cartesiano sabor×pack, con los valores del grid.
  const variaciones = [];
  for (const s of sabores) {
    for (const p of packs) {
      const v = (c.variaciones || {})[claveVariacion(s.nombre, p.nombre)] || {};
      variaciones.push({
        sabor: s.nombre, pack: p.nombre,
        precio: String(v.precio == null ? '' : v.precio),
        ...(v.stock === '' || v.stock == null ? {} : { stock: Number(v.stock) }),
        ...(v.imagenId ? { imagenId: Number(v.imagenId) } : {}),
      });
    }
  }

  const modelo = {
    nombre: form.name || '',
    estado: form.status || 'draft',
    descripcion: form.description || '',
    descripcionCorta: form.shortDescription || '',
    imagenes: (form.images || []).map((i) => i.id).filter((x) => x != null),
    crossSellIds: (form.crossSellIds || []).map(Number).filter((x) => x),
    sabores, packs, variaciones,
  };

  // Metas opcionales (solo si tienen contenido).
  if (!vacio(c.subtitulo)) modelo.subtitulo = c.subtitulo.trim();
  const filas = (c.nutricion?.filas || []).filter((f) => !vacio(f.nutriente) || !vacio(f.cantidad));
  if (!vacio(c.nutricion?.porcion) || !vacio(c.nutricion?.porciones) || filas.length) {
    modelo.nutricion = {
      porcion: (c.nutricion?.porcion || '').trim(),
      porciones: (c.nutricion?.porciones || '').trim(),
      filas: filas.map((f) => ({
        nutriente: (f.nutriente || '').trim(), cantidad: (f.cantidad || '').trim(),
        unidad: (f.unidad || '').trim(), vd: (f.vd || '').trim(),
      })),
    };
  }
  if (!vacio(c.ingredientes)) modelo.ingredientes = c.ingredientes;
  if (!vacio(c.alergenos)) modelo.alergenos = c.alergenos;
  if (!vacio(c.comoUsar)) modelo.comoUsar = c.comoUsar;
  if (!vacio(c.beneficiosDetalle)) modelo.beneficiosDetalle = c.beneficiosDetalle;
  if ((c.sellos || []).length) modelo.sellos = [...c.sellos];
  const faq = (c.faq || []).filter((x) => !vacio(x.pregunta) && !vacio(x.respuesta))
    .map((x) => ({ pregunta: x.pregunta.trim(), respuesta: x.respuesta.trim() }));
  if (faq.length) modelo.faq = faq;
  const beneficios = (c.beneficios || []).filter((b) => b.imagen && !vacio(b.titulo))
    .map((b) => ({ imagen: Number(b.imagen), titulo: b.titulo.trim(), texto: (b.texto || '').trim() }));
  if (beneficios.length) modelo.beneficios = beneficios;
  const cf = c.confianza || {};
  if (!vacio(cf.titulo) || !vacio(cf.texto) || cf.imagen) {
    modelo.confianza = {
      icono: (cf.icono || '').trim(), imagen: Number(cf.imagen) || 0, titulo: (cf.titulo || '').trim(),
      fecha: (cf.fecha || '').trim(), texto: (cf.texto || '').trim(),
      enlace_texto: (cf.enlace_texto || '').trim(), enlace_url: (cf.enlace_url || '').trim(),
    };
  }
  if (!vacio(c.video)) modelo.video = c.video.trim();
  if (c.videoPortada) modelo.videoPortada = Number(c.videoPortada);

  return modelo;
}
